import { cloneDeep } from 'lodash/fp'

// Auth selectors for backwards compatibility.
import { getSubscriptionInfo as getAuthSubscriptionInfo } from '../../auth/interface/selectors'

// Utilities.
import { mergeValues } from '../../common/utils'

/**
 * Retrieves the config options provided by the subscription plugin.
 * NOTE: This is only used by CPaaS currently and won't work with
 * the old auth/subscription config on link.  It will work with the new
 * auth/subscription config on link.
 * @method getSubscriptionConfig
 * @return {Object}
 */
export function getSubscriptionConfig (state) {
  return cloneDeep(state.config.subscription)
}

/**
 * Retrieves the maximum number of times this client will attempt to subscribe, while getting a
 * 'Service Unavailable' response from backend.
 * @method getServiceUnavailableMaxRetries
 * @param {Object} state Current state object.
 * @return {number}
 */
export function getServiceUnavailableMaxRetries (state) {
  return state.config.subscription.serviceUnavailableMaxRetries
}

/**
 * Retrieves the list of services that plugins have registered for.
 * @method getRegisteredServices
 * @return {Array}
 */
export function getRegisteredServices (state) {
  return cloneDeep(state.subscription.registeredServices)
}

/**
 * Retrieves the open notifications channels' info and their active subscriptions.
 * @method getNotificationChannels
 * @return {Object}
 */
// TODO: Rename this selector. It gets more than notification channels.
export function getNotificationChannels (state) {
  return cloneDeep({
    notificationChannels: state.subscription.notificationChannels,
    subscriptions: state.subscription.subscriptions
  })
}

/**
 * Retrieve the list of services with current subscriptions.
 * @method getSubscribedServices
 * @param {string} type the type of subscription we want to get specifically
 * @return {Array}
 */
export function getSubscribedServices (state, type) {
  const info = getSubscriptionInfo(state)
  // For backwards compatibility, make sure that this is an array. It isn't when
  //    the old Auth plugin is being used (eg. Link v3.X).
  let subscriptions = Array.isArray(info) ? info : [info]

  // If a type was specified, filter out subscriptions of other types.
  if (type) {
    subscriptions = subscriptions.filter(subscription => subscription.channelType === type)
  }

  // Massage the subscriptions to be a list of service names.

  // For Link, subscription.service is an array of strings (all services).
  // For CPaaS, subscription.service is a string (single service).
  // Process the subscriptions in a way that will provide an array of strings in
  //    both cases.
  return subscriptions.reduce((acc, currentSub) => {
    return acc.concat(currentSub.service)
  }, [])
}

/**
 * Retrieves the subscription information.
 * @method getSubscriptionInfo
 * @return {Object}
 */
export function getSubscriptionInfo (state) {
  if (state.subscription) {
    return cloneDeep(state.subscription.subscriptions)
  } else {
    // For backwards compatibility, also check if the authentication substate
    //    has subscription info. It will have the info when the oldAuth plugin
    //    is being used (eg. Link v3.X).
    // Warning: This returns an object, unlike the above which returns an array.
    return cloneDeep(getAuthSubscriptionInfo(state))
  }
}

/**
 * Retrieve the list of subscriptions matching a given channel type and service name.
 * @method getSubscriptions
 * @param {string} service the name of the service we want to get specifically (e.g. smsinbound)
 * @param {string} type the type of communication channel we want to get specifically (e.g. websocket)
 * @return {Array}
 */
export function getSubscriptions (state, service, type) {
  let subscriptions = state.subscription.subscriptions
  subscriptions = subscriptions.filter(
    subscription => subscription.service === service && subscription.channelType === type
  )
  return cloneDeep(subscriptions)
}

/**
 * Retrieve the subscription expiry time from config.
 * @method getSubscriptionExpiry
 * @return {number}
 */
export function getSubscriptionExpiry (state) {
  const subConfig = state.config.subscription
  const authConfig = state.config.authentication

  // In order to maintain backwards compatibility with the auth plugin config
  // we need to first check if this setting is provided in the authentication plugin
  // config, and if not use the one from subscription plugin.  We need to check
  // authentication config first because if no value is provided in the subscription
  // plugin, a default value will be used and we don't want that if one is provided in
  // the authentication plugin.
  const expires =
    authConfig.subscription && authConfig.subscription.expires ? authConfig.subscription.expires : subConfig.expires

  return expires
}

/**
 * Retrieve the websocket info from config. We merge the websocket config
 * from auth plugin with the one from subscription plugin giving precedence to
 * the websocket config in auth plugin since we need to maintain backwards compatibility
 * with the old format config.
 * @method getSubscriptionExpiry
 * @return {number}
 */
export function getWebsocketConfig (state) {
  const subConfig = state.config.subscription
  const authConfig = state.config.authentication

  // In order to support legacy configurations, we need to do the following:
  // Take the values (including defaults) from subscription config.  Merge into
  // that any client provided values from the authentication config.  All keys existing
  // in authentication config will overwrite those from subscription config.  However,
  // since defaults are not set for websocket in the authentication plugin, only client
  // provided values for websocket will be used from authentication config and defaults
  // will come from the subscription plugin.
  return mergeValues(subConfig.websocket, authConfig.websocket)
}

/**
 * Return the pending operation if one is pending.
 * @method getPendingOperation
 * @return {string}
 */
export function getPendingOperation (state) {
  return state.subscription.pendingOperation
}
